/**
 * Search autocomplete (SFD A9.2).
 *
 * Suggests whole words from 3 characters typed, 7 at most. Picking one fills the
 * field and submits the search — the SFD explicitly rules out linking straight to a
 * result from here, for accessibility reasons.
 */
export default class SearchAutocomplete {
  static MIN_CHARS = 3;

  static DEBOUNCE_MS = 200;

  static init() {
    document.querySelectorAll('[data-autocomplete]').forEach((root) => {
      new SearchAutocomplete(root);
    });
  }

  constructor(root) {
    this.root = root;
    this.input = root.querySelector('[data-autocomplete-input]');
    this.list = root.querySelector('[data-autocomplete-list]');

    if (!this.input || !this.list || !window.crcSearch?.ajaxUrl) {
      return;
    }

    this.activeIndex = -1;
    this.timer = null;
    this.controller = null;

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeyDown(event));

    document.addEventListener('click', (event) => {
      if (!this.root.contains(event.target)) {
        this.close();
      }
    });
  }

  onInput() {
    const term = this.input.value.trim();

    clearTimeout(this.timer);

    if (term.length < SearchAutocomplete.MIN_CHARS) {
      this.close();
      return;
    }

    this.timer = setTimeout(() => this.fetch(term), SearchAutocomplete.DEBOUNCE_MS);
  }

  async fetch(term) {
    // Drop the answer to the previous keystroke: it is already out of date.
    this.controller?.abort();
    this.controller = new AbortController();

    const url = new URL(window.crcSearch.ajaxUrl, window.location.origin);
    url.searchParams.set('action', 'crc_search_suggest');
    url.searchParams.set('q', term);

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      const payload = await response.json();

      this.render(payload?.data?.suggestions ?? []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.close();
      }
    }
  }

  render(suggestions) {
    if (!suggestions.length) {
      this.close();
      return;
    }

    this.list.innerHTML = '';
    this.activeIndex = -1;

    suggestions.forEach((word, index) => {
      const option = document.createElement('li');

      option.className = 'hero-search__suggestion';
      option.id = `search-suggestion-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.textContent = word;
      option.addEventListener('click', () => this.choose(word));

      this.list.appendChild(option);
    });

    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  onKeyDown(event) {
    const options = [...this.list.children];

    if (this.list.hidden || !options.length) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlight(Math.min(this.activeIndex + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlight(Math.max(this.activeIndex - 1, 0));
        break;
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.choose(options[this.activeIndex].textContent.trim());
        }
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  highlight(index) {
    [...this.list.children].forEach((option, i) => {
      const active = i === index;

      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.activeIndex = index;
    this.input.setAttribute('aria-activedescendant', `search-suggestion-${index}`);
  }

  choose(word) {
    this.input.value = word;
    this.close();
    this.input.form?.submit();
  }

  close() {
    this.list.hidden = true;
    this.list.innerHTML = '';
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }
}
